import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check, Flag, Home, Plus } from 'lucide-react';
import { Button } from '@shared/ui/primitives/Button';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { pageCopy } from '@shared/lib/pageCopy';
import { fetchMyProperties, type MemberProperty } from '@features/properties/api';

/**
 * Select-residence screen — shown after the welcome tour. Lists the
 * properties matched to the resident's ID; they pick the one to manage
 * first, then continue to the dashboard.
 */
export function SelectProperty(): JSX.Element {
  const copy = pageCopy().selectProperty;
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(null);

  const query = useQuery({ queryKey: ['my-properties'], queryFn: fetchMyProperties });
  const properties = query.data ?? [];

  return (
    <div className="flex min-h-full flex-col bg-paper px-6 pt-safe-or-6 pb-safe-or-6">
      <Flag className="h-6 w-6 text-brass" strokeWidth={1.75} />

      <span className="mt-6 text-[0.6875rem] font-semibold uppercase tracking-[0.16em] text-brass">
        {copy.eyebrow}
      </span>
      <h1 className="display-serif mt-2 text-display-lg leading-tight text-ink">{copy.title}</h1>
      {copy.subtitle ? (
        <p className="mt-2 max-w-sm text-body text-ink-secondary">{copy.subtitle}</p>
      ) : null}

      <div className="mt-6 flex flex-col gap-3">
        {query.isLoading ? (
          [0, 1].map((i) => <Skeleton key={i} className="h-24" />)
        ) : properties.length > 0 ? (
          properties.map((property) => (
            <PropertyCard
              key={property.property_id}
              property={property}
              selected={selected === property.property_id}
              onSelect={() => setSelected(property.property_id)}
            />
          ))
        ) : (
          <div className="rounded-lg border border-dashed border-hairline-strong bg-elevated px-4 py-8 text-center">
            <Home className="mx-auto h-7 w-7 text-ink-muted" strokeWidth={1.5} />
            <p className="mt-2 text-body-sm text-ink-secondary">
              No residences matched yet — add one to get started.
            </p>
          </div>
        )}

        <button
          type="button"
          onClick={() => navigate('/app/my-properties')}
          className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-hairline-strong bg-transparent py-3.5 text-body-sm font-medium text-ink-secondary transition-colors hover:bg-sunken"
        >
          <Plus className="h-4 w-4" strokeWidth={1.75} />
          Add another property
        </button>
      </div>

      <div className="mt-auto pt-8">
        <Button
          variant="primary"
          size="lg"
          width="full"
          disabled={properties.length > 0 && !selected}
          onClick={() => navigate('/app/home', { replace: true })}
        >
          Continue to dashboard
        </Button>
      </div>
    </div>
  );
}

function PropertyCard({
  property,
  selected,
  onSelect,
}: {
  property: MemberProperty;
  selected: boolean;
  onSelect: () => void;
}): JSX.Element {
  const verified = property.status === 'active' || property.status === 'approved';
  const details = [property.type_name, property.region, property.address]
    .filter(Boolean)
    .join(' · ');

  return (
    <button
      type="button"
      onClick={onSelect}
      className={
        'flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ' +
        (selected
          ? 'border-primary-600 bg-primary-50'
          : 'border-hairline-strong bg-elevated')
      }
    >
      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md bg-sunken">
        {property.thumbnail_url ? (
          <img src={property.thumbnail_url} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-primary-50">
            <Home className="h-6 w-6 text-primary-600" strokeWidth={1.5} />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="font-display text-headline text-ink truncate">
            {property.unit_label}
          </p>
          {property.status ? (
            <span
              className={
                'shrink-0 rounded-pill px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wide ' +
                (verified ? 'bg-primary-50 text-primary-700' : 'bg-warning/15 text-warning')
              }
            >
              {verified ? 'Verified' : 'Pending'}
            </span>
          ) : null}
        </div>
        {property.estate_name ? (
          <p className="text-body-sm text-ink-secondary truncate">{property.estate_name}</p>
        ) : null}
        {details ? <p className="text-caption text-ink-muted truncate">{details}</p> : null}
      </div>

      <span
        className={
          'flex h-6 w-6 shrink-0 items-center justify-center rounded-pill border ' +
          (selected ? 'border-primary-600 bg-primary-600 text-white' : 'border-hairline-strong')
        }
      >
        {selected ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
      </span>
    </button>
  );
}
